<template>
  <PopOverMenu
    :open="isOpen"
    :placement="placement"
    class="popup-menu-select-field"
    :middleware="dropDownMiddleware"
    @click:outside="closeMenu"
  >
    <template #target>
      <slot
        name="trigger"
        :open="openMenu"
        :close="closeMenu"
        :select-next="() => selectOption(true)"
        :select-prev="() => selectOption(false)"
        :selected-id="selectedId"
      >
        <div
          ref="selectFieldRef"
          class="select-field"
          :class="{
            'select-field--open': isOpen,
            'select-field--disabled': disabled,
            'select-field--wrap': multiple,
            [`select-field--size-${size}`]: true,
            'select-field--with-search-icon': showIconPrepend,
          }"
          :data-selected="selectedText"
          tabindex="0"
          v-bind="$attrs"
          aria-role="listbox"
          data-test="select-field"
          @mousewheel="onMouseWheel"
          @click="onClick"
          @focus="onFocus"
        >
          <div
            v-if="showIconPrepend && (!multiple || !hasValue)"
            class="select-field__icon select-field__icon--prepend"
          >
            <slot name="icon-prepend">
              <IconMonoSearch />
            </slot>
          </div>
          <div
            class="select-field__input"
            :class="{ 'select-field__input--filterable': filterable }"
          >
            <slot
              name="label"
              :selected-text="selectedText"
            >
              <TextInput
                ref="inputRef"
                :model-value="multiple ? filter : selectedText || filter"
                :placeholder="computedPlaceholder"
                :auto-focus="autoFocus"
                :show-label="false"
                :show-detail="false"
                :disabled="disabled"
                :required="required"
                :items="textInputValue"
                :multiple="multiple"
                :size="size"
                :readonly="!filterable"
                @update:model-value="onFilterChange"
              >
                <!-- slot 'active-option' is used only when `isMultiple = true` -->
                <template #item="{ item }">
                  <slot
                    name="active-option"
                    :option="item"
                  />
                </template>
              </TextInput>
            </slot>
          </div>
          <div class="select-field__icon select-field__icon--append">
            <slot name="icon-append">
              <Icon
                class="select-field__icon select-field__icon__chevron"
                :class="{ 'select-field__icon__chevron--open': isOpen }"
                name="mono-chevron-down"
              />
            </slot>
          </div>
        </div>
      </slot>
    </template>
    <template #menu>
      <CardSection
        class="select-field__content-wrapper"
        @click.prevent
      >
        <div
          v-if="$slots['header']"
          class="popup-menu-select-field__header"
        >
          <slot name="header" />
        </div>

        <VirtualList
          v-if="filteredOptions.length"
          ref="virtualListRef"
          class="popup-menu__list"
          :items="filteredOptions"
          :style="virtualListStyle"
        >
          <template #item="{ item, index }">
            <slot
              name="option-header"
              :option="item"
              :index="index"
            />
            <div @click.stop="onSelect(item)">
              <slot
                name="option"
                :option="item"
                :selected="isSelected(item)"
              />
            </div>
          </template>
        </VirtualList>
        <div
          v-else
          class="select-field__no-options"
        >
          <slot name="no-options">Sorry, no matching options</slot>
        </div>
      </CardSection>

      <div v-if="$slots['footer']">
        <slot name="footer" />
      </div>
    </template>
  </PopOverMenu>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import { computed, ref, watch, nextTick } from 'vue'
import { onKeyDown } from '@/uiKit/useHotkey'
import Icon from '@/uiKit/Icon/Icon.vue'

import IconMonoSearch from '@/assets/icons/IconMonoSearch.vue'
import VirtualList from '@/uiKit/List/VirtualList.vue'
import type { OptionType } from '@/uiKit/types'
import TextInput from '@/uiKit/TextInput/TextInput.vue'
import { getShiftedCircularIdx } from '@/core/utils/array'
import PopOverMenu from '@/uiKit/PopOverMenu.vue'
import type { Placement } from '@floating-ui/vue'
import CardSection from '@/uiKit/Card/CardSection.vue'
import { PopOverMiddleware } from '@/uiKit/popOver.utils'
import { DEFAULT_OPTION_HEIGHT } from '@/uiKit/constants'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    value?: string | OptionType[]
    options: OptionType[]
    placeholder?: string
    required?: boolean
    filterable?: boolean
    disabled?: boolean
    multiple?: boolean
    showIconPrepend?: boolean
    autoSelectFirstOption?: boolean
    scrollSelectedIntoView?: boolean
    hotkeysEnabled?: boolean
    autoFocus?: boolean
    itemHeight?: number
    size: 'sm' | 'md'
    placement?: Placement
    /** Controls wether the popup menu has same width as the trigger/target **/
    matchTriggerWidth?: boolean
  }>(),
  {
    value: '',
    placeholder: 'Search',
    required: false,
    filterable: false,
    disabled: false,
    multiple: false,
    showIconPrepend: true,
    autoSelectFirstOption: false,
    scrollSelectedIntoView: false,
    hotkeysEnabled: true,
    autoFocus: false,
    itemHeight: undefined,
    placement: 'bottom-start',
    matchTriggerWidth: true,
  },
)

// eslint-disable-next-line vue/define-emits-declaration
const emit = defineEmits(['input', 'search', 'change', 'open', 'close', 'delete', 'enter'])

const MAX_VIRTUAL_LIST_HEIGHT = 320
const isOpen = ref(false)
const selectFieldRef = ref<HTMLInputElement>()
const virtualListRef = ref<ComponentPublicInstance>()
const filter = ref('')
const selectedId = ref('')

const filteredOptions = computed<OptionType[]>(() =>
  props.options.filter((x) =>
    [...(x.alsoFilterableBy ?? []), x.text ?? '']
      .map((x) => x.toLowerCase())
      .some((x) => x.includes(filter.value.toLowerCase())),
  ),
)

/**
 * Calculates the height for the virtual scroller, setting a max height of 320px
 */
const virtualListStyle = computed(() => {
  const itemHeight = props.itemHeight || DEFAULT_OPTION_HEIGHT
  const allItemsHeight = filteredOptions.value.length * itemHeight
  return {
    minHeight: `${Math.min(allItemsHeight, MAX_VIRTUAL_LIST_HEIGHT)}px`,
  }
})

const dropDownMiddleware = computed(() =>
  props.matchTriggerWidth === true ? [PopOverMiddleware.SameWidthAsTarget] : [],
)

const ensureVisible = (): void => {
  const selectedItem = virtualListRef.value?.$el.getElementsByClassName('list-item--selected')[0]

  if (!selectedItem) {
    return
  }

  selectedItem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
}

const closeMenu = (): void => {
  isOpen.value = false
  emit('close')
}
const openMenu = (): void => {
  isOpen.value = true
  emit('open')
}
const toggleMenu = (): void => {
  isOpen.value = !isOpen.value

  if (props.scrollSelectedIntoView) {
    nextTick(ensureVisible)
  }
}

const textInputValue = computed<string[] | OptionType[]>(() =>
  Array.isArray(props.value) ? props.value : [props.value],
)

// focus input on container click
// as the container can grow bigger than the nested input
const inputRef = ref<InstanceType<typeof TextInput> | null>(null)
const onClick = (): void => {
  if (props.disabled) {
    return
  }
  openMenu()
  inputRef.value?.setFocus()
}

const onFocus = (): void => {
  if (props.disabled) {
    return
  }
  openMenu()
  inputRef.value?.setFocus()
}

const selectedIdx = computed((): number =>
  filteredOptions.value.findIndex(({ id }) => id === selectedId.value),
)

const selectedText = computed(
  () => filteredOptions.value.find(({ id }) => id === selectedId.value)?.text ?? '',
)

const optionsLength = computed(() => filteredOptions.value.length)

const hasValue = computed((): boolean =>
  Array.isArray(props.value) ? props.value.length > 0 : !!props.value,
)

const computedPlaceholder = computed(() => {
  if (hasValue.value && !props.multiple && typeof props.value === 'string') {
    return selectedText.value
  }
  // avoid showing placeholder when multiple &
  // at least 1 item is selected
  if (props.multiple && Array.isArray(props.value) && props.value.length > 0) {
    return ''
  }

  return props.placeholder
})

const isSelected = (option: OptionType): boolean => option.id === selectedId.value

const onFilterChange = (val: string): void => {
  emit('input', '')
  emit('search', val)
  filter.value = val
  selectedId.value = props.autoSelectFirstOption ? (filteredOptions.value[0]?.id ?? '') : ''
}

const onSelect = (option: OptionType): void => {
  // Don't allow selection of disabled options
  if (option.disabled) {
    return
  }

  filter.value = ''
  selectedId.value = option?.id || ''

  emit('change', option)
  emit('input', option)

  if (!props.multiple) {
    closeMenu()
  }
}

const updateSelectedId = (): void => {
  const option = filteredOptions.value.find(({ id }) => id === props.value)

  if (props.autoSelectFirstOption) {
    selectedId.value = option?.id || filteredOptions.value[0]?.id || ''
    return
  }

  selectedId.value = option?.id || ''
}

watch(
  () => [props.value, props.options],
  () => {
    // set selectedText to the selected option's text
    updateSelectedId()

    // useful to reposition the PopupMenu in case the
    // trigger size changes
    // repositionMenu()
  },
  { immediate: true },
)

const nextNonSeparatorIdx = (increase: boolean, skipped: number): number => {
  // Guard against infinite recursion when all options are disabled or separators
  if (skipped >= optionsLength.value) {
    return selectedIdx.value // Return current index to prevent infinite recursion
  }

  // this is called recursively with a greater and greater skip amount
  // until we skip over all subsequent separator and disabled items
  const assumedCurrentIndex = selectedIdx.value + (increase ? skipped : -skipped)
  const newIdx = getShiftedCircularIdx(assumedCurrentIndex, optionsLength.value, increase ? 1 : -1)

  return filteredOptions.value[newIdx]?.separator || filteredOptions.value[newIdx]?.disabled
    ? nextNonSeparatorIdx(increase, skipped + 1)
    : newIdx
}

const selectOption = (next = false): void => {
  // if the popup menu is closed, open it
  // but don't select a new option as that's
  // confusing for the user as only one thing happening
  // at once it's preferred there
  if (!isOpen.value) {
    toggleMenu()
    return
  }
  const newIdx = nextNonSeparatorIdx(next, 0)
  selectedId.value = filteredOptions.value[newIdx]?.id || ''
  emit('input', filteredOptions.value[newIdx])
}

const onMouseWheel = (event: WheelEvent): void => {
  if (props.multiple || !props.hotkeysEnabled) {
    return
  }

  event.stopPropagation()
  event.preventDefault()

  const { deltaY } = event
  selectOption(deltaY > 0)
}

const resetFilter = (): void => {
  filter.value = ''
  selectedId.value = ''
}

if (props.hotkeysEnabled) {
  // Select next option
  onKeyDown(
    ['ArrowDown', 'ArrowRight'],
    () => {
      selectOption(true)

      if (props.scrollSelectedIntoView) {
        ensureVisible()
      }
    },
    // important not to pass the .value there
    { target: selectFieldRef },
  )

  // Select prev option
  onKeyDown(
    ['ArrowUp', 'ArrowLeft'],
    () => {
      selectOption(false)

      if (props.scrollSelectedIntoView) {
        ensureVisible()
      }
    },
    // important not to pass the .value there
    { target: selectFieldRef },
  )

  // Submit current option
  onKeyDown(
    ['Enter'],
    (e: KeyboardEvent) => {
      e.stopPropagation()
      e.preventDefault()
      emit('enter')
      const selected = filteredOptions.value[selectedIdx.value]
      if (!selected || selected.disabled) {
        return
      }
      onSelect(selected)
    },
    // important not to pass the .value there
    { target: selectFieldRef },
  )

  // Close the PopupMenu
  onKeyDown(
    ['Escape', 'Tab'],
    (e: KeyboardEvent) => {
      e.stopPropagation()
      e.preventDefault()
      closeMenu()
      inputRef.value?.blur()
    },
    // important not to pass the .value there
    { target: selectFieldRef },
  )

  // Close the PopupMenu
  onKeyDown(
    ['Backspace'],
    (e: KeyboardEvent) => {
      // prevent backspace from being handled by other shortcuts
      e.stopPropagation()
      // when multiple
      // and has selected items
      // and text input value is empty
      // handle backspaces as deleting the last applied value
      if (
        !props.multiple ||
        filter.value.length > 0 ||
        !Array.isArray(props.value) ||
        props.value.length === 0
      ) {
        return
      }

      const last = props.value.at(-1)
      if (!last) {
        return
      }

      emit('delete', last)
    },
    // important not to pass the .value there
    { target: selectFieldRef },
  )
}

const scrollInputIntoView = (): void => {
  inputRef.value?.scrollIntoView()
}

const scrollSelectedOptionsToStart = (): void => {
  inputRef.value?.scrollContainerToStart()
}

defineExpose({
  resetFilter,
  scrollInputIntoView,
  scrollSelectedOptionsToStart,
})
</script>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
@import '@/assets/styles/shared/scrollbarV2';
@import '@/uiKit/assets/heights.scss';
@import '@/uiKit/assets/spacings.scss';
@import '@/uiKit/assets/typography.scss';

.popup-menu-select-field {
  &__header {
    @include col;
    justify-content: center;
    min-height: $heightItem;
    padding: 0 $spacing-2;
    color: var(--color-text-subtle);
    @include typographyRegularBody200;
  }
}
</style>

<style lang="scss" scoped>
@import '@/uiKit/assets/borders.scss';
@import '@/uiKit/assets/colors.scss';
@import '@/uiKit/assets/spacings.scss';
@import '@/uiKit/assets/typography.scss';
@import '@/assets/styles/shared/scrollbarV2';

$height: 36px;

.popup-menu-select-field {
  display: block;

  .popup-menu__list {
    max-height: 40vh;
  }
}

.select-field {
  position: relative;
  @include row--distributed--center;
  flex: 1;
  gap: $spacing-2;
  height: $height;
  min-height: $height;
  max-height: $height;
  padding: $spacing-3 $spacing-4;
  border-radius: $borderRadius8;
  border: none;
  outline: 1px solid var(--color-border-input);
  cursor: pointer;

  &--size {
    &-sm {
      height: $h-sm;
      min-height: $h-sm;
      max-height: $h-sm;
    }
  }

  &--with-search-icon {
    padding: $spacing-2;
  }

  &--wrap {
    max-height: 360px;
    height: auto;

    .select-field__input {
      @include scrollbarV2;
      outline: none;
    }
  }

  &:hover:not(:active):not(:focus):not(.select-field--disabled) {
    background-color: var(--color-background-transparent-hovered);
  }

  &--disabled {
    background-color: var(--color-background-disabled);
    cursor: not-allowed;
  }

  &--open,
  &:active:not(.select-field--disabled),
  &:focus:not(.select-field--disabled) {
    outline: 1.5px solid var(--color-border-focused);
    caret-color: var(--color-icon-primary);
    color: var(--color-text-default);
    outline-width: 1.5px;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;

    &--prepend {
      padding-left: $spacing-2;
    }

    &__chevron {
      color: var(--color-icon-subtlest);

      &--open {
        transform: rotateZ(-180deg);
      }
    }
  }

  &__input {
    @include row;
    align-items: center;
    flex: 1 1 auto;
    height: 100%;
    background-color: transparent;
    color: var(--color-text-default) !important;
    cursor: pointer;
    user-select: none;
    outline: none;
    pointer-events: none;

    &:focus,
    &:active {
      outline: none;
    }

    :deep(.text-input__container) {
      padding: 0;
      outline: none;
      background: transparent;
      height: auto;

      &:hover {
        background: transparent;
      }
    }

    &--filterable {
      pointer-events: all;
    }

    &__label {
      text-transform: capitalize;
      @include typographyRegularBody200;
      @include truncate;
      color: var(--color-text-default);
    }
  }

  &__no-options {
    height: 100%;
    min-height: 30px;
    padding: 15px 20px;
    @include typographyRegularBody200;
    @include truncate();
    color: var(--color-text-default);
    text-align: center;
    word-break: break-word;
    white-space: break-spaces;
  }
}
</style>
